import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Truck, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DriverLoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', confirmPassword: '', invite_code: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await api.post('/driver/auth/login', loginForm);
      
      // Store driver token
      localStorage.setItem('driver_token', res.data.access_token);
      localStorage.setItem('driver_info', JSON.stringify(res.data.driver));
      
      toast.success(`Welcome back, ${res.data.driver.first_name}!`);
      navigate('/driver/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await api.post('/driver/auth/register', {
        email: registerForm.email,
        password: registerForm.password,
        invite_code: registerForm.invite_code
      });
      
      // Store driver token
      localStorage.setItem('driver_token', res.data.access_token);
      localStorage.setItem('driver_info', JSON.stringify(res.data.driver));
      
      toast.success('Account created! Welcome to HaulLogix Driver.');
      navigate('/driver/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#071423] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#22C55E] mb-4">
            <Truck className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-2xl font-bold text-white">HaulLogix Driver</h1>
          <p className="text-gray-400 mt-1">Manage your loads on the go</p>
        </div>

        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">
              {isLogin ? 'Driver Sign In' : 'Create Account'}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? 'Enter your credentials to access your loads'
                : 'Use the invite code from your carrier'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLogin ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                    placeholder="driver@example.com"
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      placeholder="••••••••"
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Sign In
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="invite_code">Invite Code</Label>
                  <Input
                    id="invite_code"
                    value={registerForm.invite_code}
                    onChange={(e) => setRegisterForm({...registerForm, invite_code: e.target.value.toUpperCase()})}
                    placeholder="XXXXXXXX"
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20 uppercase tracking-widest text-center font-mono"
                    maxLength={8}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Get this code from your dispatcher</p>
                </div>
                <div>
                  <Label htmlFor="reg_email">Email</Label>
                  <Input
                    id="reg_email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    placeholder="driver@example.com"
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg_password">Password</Label>
                  <Input
                    id="reg_password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                    placeholder="At least 6 characters"
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                    placeholder="••••••••"
                    className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Account
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-[#3B82F6] hover:underline"
              >
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-[#3B82F6]/10 text-center">
              <Link to="/" className="text-xs text-gray-500 hover:text-gray-400">
                ← Back to HaulLogix
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverLoginPage;
